import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logError } from "@/lib/logger";
import type { Database } from "@/integrations/supabase/types";

type JobApplicationRow = Database["public"]["Tables"]["job_applications"]["Row"];

/** Application view type – subset of job_applications row used by UI. */
export interface Application {
  id: string;
  job_id: string;
  job_title: string;
  company_name: string | null;
  applied_at: string | null;
  status: string;
  destination?: string | null;
}

function rowToApplication(row: JobApplicationRow): Application {
  return {
    id: row.id,
    job_id: row.job_id,
    job_title: row.job_title,
    company_name: row.company_name,
    applied_at: row.applied_at,
    status: row.status,
    destination: row.destination ?? undefined,
  };
}

const normalizeUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  try {
    const url = new URL(trimmed);
    const normalizedHost = url.host.toLowerCase();
    const normalizedPath = url.pathname.replace(/\/+$/, "");
    return `${url.protocol}//${normalizedHost}${normalizedPath}`;
  } catch {
    // Best-effort fallback: strip query/hash and trailing slash
    return trimmed.split(/[?#]/)[0].replace(/\/+$/, "");
  }
};

const looksLikeUrl = (value: string | null | undefined) => {
  if (!value) return false;
  const v = value.trim().toLowerCase();
  return v.startsWith("http://") || v.startsWith("https://");
};

export interface UseApplicationsOptions {
  /** When false, do not fetch on mount (e.g. Matches first-paint: use matches-initial only, then refetch after paint). Default true. */
  fetchOnMount?: boolean;
}

export const useApplications = (options: UseApplicationsOptions = {}) => {
  const { fetchOnMount = true } = options;
  const { toast } = useToast();
  const [applications, setApplications] = useState<Application[]>([]);
  const [appliedJobIds, setAppliedJobIds] = useState<Set<string>>(new Set());
  const [appliedJobUrls, setAppliedJobUrls] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const fetchApplications = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("job_applications")
      .select("*")
      .eq("user_id", session.user.id)
      .order("applied_at", { ascending: false });

    if (error) {
      logError("Error fetching applications", error);
      setLoading(false);
      return;
    }

    const rows: JobApplicationRow[] = (data ?? []) as JobApplicationRow[];

    setApplications(rows.map(rowToApplication));
    // P0-MATCH-1: Only hide jobs with status applied/applied_externally/reported_applied (or null legacy)
    const hideStatuses = new Set(["applied", "applied_externally", "reported_applied"]);
    const rowsToHide = rows.filter((app) => app.status == null || hideStatuses.has(String(app.status)));
    setAppliedJobIds(new Set(rowsToHide.map((app) => app.job_id)));

    const urlSet = new Set<string>();
    for (const app of rowsToHide) {
      if (looksLikeUrl(app.destination)) {
        const n = normalizeUrl(app.destination);
        if (n) urlSet.add(n);
      }
    }
    setAppliedJobUrls(urlSet);

    setLoading(false);
  }, []);

  useEffect(() => {
    if (!fetchOnMount) {
      setLoading(false);
      return;
    }
    fetchApplications();
  }, [fetchOnMount, fetchApplications]);

  // P0: When user returns to tab (e.g. after applying via extension), refetch so "Sökt" appears
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      setTimeout(() => fetchApplications(), 400);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [fetchApplications]);

  const markAsApplied = async (
    jobId: string,
    jobTitle: string,
    companyName?: string,
    jobUrl?: string
  ) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Fel",
        description: "Du måste vara inloggad",
        variant: "destructive",
      });
      return false;
    }

    // Check if already applied (id or url)
    const normalizedJobUrl = jobUrl && looksLikeUrl(jobUrl) ? normalizeUrl(jobUrl) : null;
    if (appliedJobIds.has(jobId) || (normalizedJobUrl && appliedJobUrls.has(normalizedJobUrl))) {
      return true;
    }

    const { error } = await supabase.from("job_applications").insert({
      user_id: session.user.id,
      job_id: jobId,
      job_title: jobTitle,
      company_name: companyName || null,
      status: "applied",
      destination: normalizedJobUrl || null,
      applied_at: new Date().toISOString(),
    });

    if (error) {
      // P0: Row exists (e.g. recordStarted already inserted). Upgrade started → applied.
      if (error.code === "23505") {
        const { error: updateError } = await supabase
          .from("job_applications")
          .update({
            status: "applied",
            applied_at: new Date().toISOString(),
            destination: normalizedJobUrl || undefined,
            job_title: jobTitle,
            company_name: companyName || null,
          })
          .eq("user_id", session.user.id)
          .eq("job_id", jobId);
        if (updateError) {
          toast({
            title: "Fel",
            description: "Kunde inte uppdatera ansökan",
            variant: "destructive",
          });
          return false;
        }
        setAppliedJobIds((prev) => new Set(prev).add(jobId));
        if (normalizedJobUrl) {
          setAppliedJobUrls((prev) => new Set(prev).add(normalizedJobUrl));
        }
        setTimeout(() => fetchApplications(), 800);
        return true;
      }
      toast({
        title: "Fel",
        description: "Kunde inte spara ansökan",
        variant: "destructive",
      });
      return false;
    }

    setAppliedJobIds((prev) => new Set(prev).add(jobId));
    if (normalizedJobUrl) {
      setAppliedJobUrls((prev) => new Set(prev).add(normalizedJobUrl));
    }

    // Do NOT refetch immediately: the DB might not yet return the new row (replica lag / commit timing),
    // and we would overwrite state with stale data and the job would reappear in the list.
    // Rely on the optimistic update for this tab; refetch after a short delay to sync from DB.
    setTimeout(() => {
      fetchApplications();
    }, 800);

    // No toast here - let the calling code handle UI feedback
    return true;
  };

  /** P0: Record that user started an external application (opens apply URL). Backend single source of truth; markAsApplied later upgrades to applied. */
  const recordStarted = useCallback(
    async (jobId: string, jobTitle: string, companyName?: string, jobUrl?: string) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return;

      const normalizedJobUrl = jobUrl && looksLikeUrl(jobUrl) ? normalizeUrl(jobUrl) : null;
      const { error } = await supabase.from("job_applications").insert({
        user_id: session.user.id,
        job_id: jobId,
        job_title: jobTitle,
        company_name: companyName || null,
        status: "started",
        destination: normalizedJobUrl || null,
        applied_at: null,
      });
      if (error && error.code !== "23505") {
        logError("recordStarted failed", error);
      }
    },
    []
  );

  const isApplied = (jobId: string, jobUrl?: string) => {
    if (appliedJobIds.has(jobId)) return true;
    if (jobUrl && looksLikeUrl(jobUrl)) {
      const normalizedJobUrl = normalizeUrl(jobUrl);
      if (normalizedJobUrl && appliedJobUrls.has(normalizedJobUrl)) return true;
    }
    return false;
  };

  return {
    applications,
    appliedJobIds,
    appliedJobUrls,
    loading,
    markAsApplied,
    recordStarted,
    isApplied,
    refetch: fetchApplications,
  };
};
